import React, { useState, useEffect } from 'react';
import { Discount, DiscountUsageStats } from '../../types/discount';
import { discountService } from '../../services/discountService';
import LineChart from '../Charts/LineChart';
import BarChart from '../Charts/BarChart';
import MetricCard from '../Common/MetricCard';

interface DiscountAnalyticsProps {
  discount: Discount;
  onClose: () => void;
}

const DiscountAnalytics: React.FC<DiscountAnalyticsProps> = ({
  discount,
  onClose
}) => {
  const [stats, setStats] = useState<DiscountUsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, [discount._id]);

  const loadStats = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await discountService.getDiscountUsageStats(discount._id);
      
      if (response.success && response.data) {
        setStats(response.data);
      } else {
        setError(response.error?.message || 'Failed to load discount statistics');
      }
    } catch (err) {
      setError('Failed to load discount statistics');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatMonth = (monthData: { year: number; month: number }): string => {
    const date = new Date(monthData.year, monthData.month - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  const getUsagePercentage = (): number => {
    if (discount.usageLimit === 0) return 0;
    return Math.round((discount.usageCount / discount.usageLimit) * 100);
  };

  const getBudgetPercentage = (): number => {
    if (discount.budgetLimit === 0) return 0;
    return Math.round((discount.budgetUsed / discount.budgetLimit) * 100);
  };

  const getPerformanceMetrics = () => {
    if (!stats) return null;

    const averageDiscountPerUse = stats.totalUsage > 0 
      ? stats.totalDiscountAmount / stats.totalUsage 
      : 0;

    const roi = stats.totalRevenueLoss > 0 
      ? ((stats.totalUsage * averageDiscountPerUse) / stats.totalRevenueLoss) * 100 
      : 0;

    return {
      averageDiscountPerUse,
      roi,
      conversionRate: stats.totalUsage > 0 ? (stats.totalUsage / 1000) * 100 : 0 // Placeholder calculation
    };
  };

  const prepareUsageChartData = () => {
    if (!stats?.usageByMonth) return [];

    const sortedData = [...stats.usageByMonth].sort((a, b) => {
      if (a._id.year !== b._id.year) return a._id.year - b._id.year;
      return a._id.month - b._id.month;
    });

    return sortedData.map(item => ({
      month: formatMonth(item._id),
      usageCount: item.count,
      discountAmount: item.totalDiscount
    }));
  };

  const prepareTopCustomersData = () => {
    if (!stats?.topCustomers) return [];

    return stats.topCustomers.map((customer, index) => ({
      customer: `Customer ${index + 1}`,
      usageCount: customer.usageCount,
      totalSavings: customer.totalSavings
    }));
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Error</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={loadStats}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Retry
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const performanceMetrics = getPerformanceMetrics();
  const usageChartData = prepareUsageChartData();
  const topCustomersData = prepareTopCustomersData();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Discount Analytics: {discount.name}
            </h2>
            <p className="text-sm text-gray-500">Code: {discount.code}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Overview Metrics */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <MetricCard
                title="Total Usage"
                value={stats?.totalUsage.toString() || '0'}
                change={getUsagePercentage() > 0 ? { value: getUsagePercentage(), type: 'increase' } : undefined}
              />
              <MetricCard
                title="Total Discount Given"
                value={formatCurrency(stats?.totalDiscountAmount || 0)}
              />
              <MetricCard
                title="Revenue Impact"
                value={formatCurrency(stats?.totalRevenueLoss || 0)}
              />
              <MetricCard
                title="Budget Used"
                value={formatCurrency(discount.budgetUsed)}
                change={getBudgetPercentage() > 0 ? { value: getBudgetPercentage(), type: 'increase' } : undefined}
              />
            </div>
          </div>

          {/* Performance Metrics */}
          {performanceMetrics && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard
                  title="Average Discount per Use"
                  value={formatCurrency(performanceMetrics.averageDiscountPerUse)}
                />
                <MetricCard
                  title="ROI"
                  value={`${performanceMetrics.roi.toFixed(1)}%`}
                />
                <MetricCard
                  title="Conversion Rate"
                  value={`${performanceMetrics.conversionRate.toFixed(1)}%`}
                />
              </div>
            </div>
          )}

          {/* Usage Trends */}
          {stats?.usageByMonth && stats.usageByMonth.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Usage Trends</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <LineChart
                  data={usageChartData}
                  xAxisKey="month"
                  lines={[
                    { key: 'usageCount', color: '#3B82F6', name: 'Usage Count' },
                    { key: 'discountAmount', color: '#10B981', name: 'Discount Amount ($)' }
                  ]}
                  title="Monthly Usage and Discount Amount"
                />
              </div>
            </div>
          )}

          {/* Top Customers */}
          {stats?.topCustomers && stats.topCustomers.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Customers</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <BarChart
                    data={topCustomersData}
                    xAxisKey="customer"
                    bars={[
                      { key: 'usageCount', color: '#3B82F6', name: 'Usage Count' },
                      { key: 'totalSavings', color: '#10B981', name: 'Total Savings ($)' }
                    ]}
                    title="Top Customers by Usage"
                  />
                </div>
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Customer Details</h4>
                  {stats.topCustomers.slice(0, 5).map((customer, index) => (
                    <div key={customer._id} className="flex justify-between items-center p-3 bg-white rounded border">
                      <div>
                        <div className="font-medium">Customer #{index + 1}</div>
                        <div className="text-sm text-gray-500">ID: {customer._id}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{customer.usageCount} uses</div>
                        <div className="text-sm text-gray-500">
                          {formatCurrency(customer.totalSavings)} saved
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Discount Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Discount Details</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Basic Information</h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Type:</dt>
                      <dd className="text-gray-900 capitalize">{discount.type.replace('_', ' ')}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Value:</dt>
                      <dd className="text-gray-900">{discountService.formatDiscountValue(discount)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Status:</dt>
                      <dd className="text-gray-900 capitalize">{discount.status.replace('_', ' ')}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Valid Until:</dt>
                      <dd className="text-gray-900">{new Date(discount.validUntil).toLocaleDateString()}</dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Settings</h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Auto Apply:</dt>
                      <dd className="text-gray-900">{discount.isAutoApply ? 'Yes' : 'No'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Public:</dt>
                      <dd className="text-gray-900">{discount.isPublic ? 'Yes' : 'No'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Priority:</dt>
                      <dd className="text-gray-900">{discount.priority}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Can Combine:</dt>
                      <dd className="text-gray-900">{discount.canCombineWithOtherDiscounts ? 'Yes' : 'No'}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiscountAnalytics;